import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import {
  CREATE_PROJECT_MUTATION,
  UPDATE_PROJECT_MUTATION,
  GET_ASSIGNABLE_PROJECT_LEADERS,
  GET_PROJECT,
} from '../../graphql/queries';
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_OPTIONS,
  ProjectStatus,
  compareEmployeesByLastName,
  toDateInputValue,
} from './projectUtils';

export interface ProjectDialogProject {
  id: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  startDate?: string | null;
  plannedEndDate?: string | null;
  leaderId?: string | null;
  /** Used to keep the current leader visible in the picker even if they
   *  no longer have the projects.manage_own permission. */
  leader?: { id: string; firstName: string; lastName: string } | null;
}

interface ProjectDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided, dialog is in edit mode for this project. */
  project?: ProjectDialogProject | null;
  /** Optional callback invoked after successful save with the saved project id. */
  onSaved?: (id: string) => void;
}

interface EmployeeOption {
  id: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
}

/**
 * Create/Edit dialog for a Project.
 *
 * Validation:
 * - Name required, 2-200 chars
 * - startDate <= plannedEndDate when both provided
 *
 * Confirmation prompt on close when form is dirty.
 */
export const ProjectDialog: React.FC<ProjectDialogProps> = ({
  open,
  onClose,
  project,
  onSaved,
}) => {
  const isEdit = Boolean(project);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('ACTIVE');
  const [startDate, setStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [leaderId, setLeaderId] = useState<string>('');
  const [errors, setErrors] = useState<{
    name?: string;
    dates?: string;
    leader?: string;
    submit?: string;
  }>({});
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const { data: employeesData, loading: employeesLoading } = useQuery<{
    assignableProjectLeaders: EmployeeOption[];
  }>(GET_ASSIGNABLE_PROJECT_LEADERS, {
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });

  const [createProject, { loading: creating }] = useMutation(
    CREATE_PROJECT_MUTATION,
    {
      refetchQueries: ['GetProjectsPaginated', 'GetMyProjects'],
      awaitRefetchQueries: true,
    },
  );
  const [updateProject, { loading: updating }] = useMutation(
    UPDATE_PROJECT_MUTATION,
    {
      refetchQueries: project
        ? [
            'GetProjectsPaginated',
            'GetMyProjects',
            { query: GET_PROJECT, variables: { id: project.id } },
          ]
        : ['GetProjectsPaginated', 'GetMyProjects'],
      awaitRefetchQueries: true,
    },
  );

  const saving = creating || updating;

  // Reset form on open / project change
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (project) {
      setName(project.name || '');
      setDescription(project.description || '');
      setStatus(project.status || 'ACTIVE');
      setStartDate(toDateInputValue(project.startDate));
      setPlannedEndDate(toDateInputValue(project.plannedEndDate));
      setLeaderId(project.leaderId || '');
    } else {
      setName('');
      setDescription('');
      setStatus('ACTIVE');
      setStartDate('');
      setPlannedEndDate('');
      setLeaderId('');
    }
  }, [open, project]);

  const initial = useMemo(() => {
    if (project) {
      return {
        name: project.name || '',
        description: project.description || '',
        status: project.status || 'ACTIVE',
        startDate: toDateInputValue(project.startDate),
        plannedEndDate: toDateInputValue(project.plannedEndDate),
        leaderId: project.leaderId || '',
      };
    }
    return {
      name: '',
      description: '',
      status: 'ACTIVE' as ProjectStatus,
      startDate: '',
      plannedEndDate: '',
      leaderId: '',
    };
  }, [project]);

  const isDirty =
    name !== initial.name ||
    description !== initial.description ||
    status !== initial.status ||
    startDate !== initial.startDate ||
    plannedEndDate !== initial.plannedEndDate ||
    leaderId !== initial.leaderId;

  const activeEmployees = useMemo(() => {
    const filtered = (employeesData?.assignableProjectLeaders || []).filter(
      (e) => e.isActive,
    );
    // Keep the existing leader visible even if they no longer have the
    // permission — otherwise the picker would render an empty value.
    if (project?.leader && !filtered.some((e) => e.id === project.leader!.id)) {
      filtered.push({
        id: project.leader.id,
        firstName: project.leader.firstName,
        lastName: project.leader.lastName,
        isActive: true,
      });
    }
    return filtered.sort(compareEmployeesByLastName);
  }, [employeesData, project?.leader]);

  const validate = (): boolean => {
    const errs: typeof errors = {};
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      errs.name = 'Nazwa musi mieć co najmniej 2 znaki';
    } else if (trimmed.length > 200) {
      errs.name = 'Nazwa nie może przekraczać 200 znaków';
    }
    if (isEdit && !leaderId) {
      errs.leader = 'Lider projektu jest wymagany';
    }
    if (startDate && plannedEndDate) {
      if (new Date(startDate) > new Date(plannedEndDate)) {
        errs.dates =
          'Planowana data zakończenia nie może być wcześniejsza niż data rozpoczęcia';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const payload: any = {
        name: name.trim(),
        description: description.trim() || null,
        status,
        startDate: startDate || null,
        plannedEndDate: plannedEndDate || null,
      };
      if (leaderId) {
        payload.leaderId = leaderId;
      }

      if (isEdit && project) {
        const { data } = await updateProject({
          variables: { id: project.id, input: payload },
        });
        toast.success(`Projekt "${name.trim()}" zaktualizowany`);
        onSaved?.(data?.updateProject?.id || project.id);
      } else {
        // For create, omit nulls — backend may not accept null for some fields
        const createPayload: any = {
          name: payload.name,
          status: payload.status,
        };
        if (payload.description) createPayload.description = payload.description;
        if (payload.startDate) createPayload.startDate = payload.startDate;
        if (payload.plannedEndDate)
          createPayload.plannedEndDate = payload.plannedEndDate;
        if (payload.leaderId) createPayload.leaderId = payload.leaderId;

        const { data } = await createProject({
          variables: { input: createPayload },
        });
        toast.success(`Projekt "${name.trim()}" utworzony`);
        if (data?.createProject?.id) onSaved?.(data.createProject.id);
      }
      onClose();
    } catch (err: any) {
      const msg = err?.message || 'Nie udało się zapisać projektu';
      setErrors((prev) => ({ ...prev, submit: msg }));
      toast.error(msg);
    }
  };

  const handleClose = () => {
    if (saving) return;
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) handleClose();
        }}
      >
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="bg-background px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              {isEdit ? `Edytuj projekt: ${project?.name}` : 'Nowy projekt'}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'Zaktualizuj informacje o projekcie'
                : 'Wypełnij formularz, aby utworzyć nowy projekt'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            <form
              id="project-form"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {errors.submit && (
                <div className="text-red-600 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                  {errors.submit}
                </div>
              )}

              <div>
                <Label htmlFor="project-name">
                  Nazwa <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="project-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="np. Wdrożenie nowego systemu CRM"
                  maxLength={200}
                  required
                />
                {errors.name && (
                  <p className="text-xs text-red-600 mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="project-description">Opis</Label>
                <Textarea
                  id="project-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Krótki opis celu i zakresu projektu..."
                  rows={3}
                  maxLength={2000}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="project-status">Status</Label>
                  <Select
                    value={status}
                    onValueChange={(v) => setStatus(v as ProjectStatus)}
                  >
                    <SelectTrigger id="project-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {PROJECT_STATUS_LABELS[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="project-leader">
                    Lider projektu
                    {isEdit && <span className="text-red-500"> *</span>}
                  </Label>
                  <SearchableSelect
                    id="project-leader"
                    value={leaderId}
                    onChange={setLeaderId}
                    options={activeEmployees.map((emp) => ({
                      value: emp.id,
                      label: `${emp.lastName} ${emp.firstName}`,
                    }))}
                    placeholder={
                      employeesLoading ? 'Ładowanie...' : 'Wybierz lidera'
                    }
                    searchPlaceholder="Szukaj po imieniu/nazwisku..."
                    disabled={employeesLoading}
                  />
                  {errors.leader && (
                    <p className="text-xs text-red-600 mt-1">{errors.leader}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="project-start">Data rozpoczęcia</Label>
                  <Input
                    id="project-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="project-end">
                    Planowana data zakończenia
                  </Label>
                  <Input
                    id="project-end"
                    type="date"
                    value={plannedEndDate}
                    onChange={(e) => setPlannedEndDate(e.target.value)}
                  />
                </div>
              </div>
              {errors.dates && (
                <p className="text-xs text-red-600">{errors.dates}</p>
              )}
            </form>
          </div>

          <DialogFooter className="bg-background px-6 pb-6 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={saving}
            >
              Anuluj
            </Button>
            <Button type="submit" form="project-form" disabled={saving}>
              {saving
                ? 'Zapisywanie...'
                : isEdit
                ? 'Zapisz zmiany'
                : 'Utwórz projekt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation: discard unsaved changes */}
      <Dialog
        open={confirmDiscardOpen}
        onOpenChange={(v) => {
          if (!v) setConfirmDiscardOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Niezapisane zmiany
            </DialogTitle>
            <DialogDescription>
              Masz niezapisane zmiany w formularzu. Czy na pewno chcesz zamknąć
              okno bez zapisywania?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDiscardOpen(false)}
            >
              Wróć do edycji
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDiscardOpen(false);
                onClose();
              }}
            >
              Odrzuć zmiany
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
